import React from 'react';
import type ReactECharts from 'echarts-for-react';
import { useTranslation } from 'react-i18next';
import { Download, FileImage, FileSpreadsheet, FileText } from 'lucide-react';

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import {
  exportChartToPNG,
  exportToCSV,
  exportToXLSX,
  extractChartData,
  type ExportOptions,
  type ChartExportOptions
} from '@/shared/lib/export';

interface ChartExportMenuProps {
  chartRef: React.RefObject<ReactECharts | null>;
  chartOption: Record<string, unknown>;
  exportOptions?: {
    png?: ChartExportOptions;
    csv?: ExportOptions;
    xlsx?: ExportOptions;
  };
  disabled?: boolean;
  className?: string;
}

/**
 * Export menu component for ECharts
 * @example
 * const chartRef = useRef<ReactECharts>(null);
 * <ChartExportMenu
 *   chartRef={chartRef}
 *   chartOption={chartOption}
 *   exportOptions={{
 *     png: { filename: 'chart.png', pixelRatio: 2 },
 *     csv: { filename: 'data.csv' }
 *   }}
 * />
 */
export const ChartExportMenu: React.FC<ChartExportMenuProps> = ({
  chartRef,
  chartOption,
  exportOptions,
  disabled = false,
  className
}) => {
  const { t } = useTranslation();
  const handleExportPNG = () => {
    const instance = chartRef.current?.getEchartsInstance();
    exportChartToPNG(instance, exportOptions?.png);
  };

  const handleExportCSV = () => {
    const data = extractChartData(chartOption);
    exportToCSV(data, exportOptions?.csv);
  };

  const handleExportXLSX = () => {
    const data = extractChartData(chartOption);
    exportToXLSX(data, exportOptions?.xlsx);
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm" disabled={disabled} className={className}>
          <Download />
          <span className="hidden md:inline">{t('common.export.label', 'Export')}</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onClick={handleExportPNG} data-testid="export-png">
          <FileImage className="h-4 w-4 mr-2" />
          {t('common.export.png', 'Export as PNG')}
        </DropdownMenuItem>
        <DropdownMenuItem onClick={handleExportCSV} data-testid="export-csv">
          <FileText className="h-4 w-4 mr-2" />
          {t('common.export.csv', 'Export as CSV')}
        </DropdownMenuItem>
        <DropdownMenuItem onClick={handleExportXLSX} data-testid="export-xlsx">
          <FileSpreadsheet className="h-4 w-4 mr-2" />
          {t('common.export.xlsx', 'Export as Excel')}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
